import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { 
  ArrowLeft,
  Clock,
  CheckCircle,
  XCircle,
  Trophy,
  Calendar,
  BarChart3,
  Eye,
  MessageSquare,
  Sparkles
} from "lucide-react";
import { motion } from "framer-motion";

export default function QuizHistory() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/quiz-history`, { headers });
      setHistory(response.data);
    } catch (error) {
      toast.error("Failed to load quiz history");
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 90) return "text-green-600 bg-green-100";
    if (percentage >= 80) return "text-blue-600 bg-blue-100";
    if (percentage >= 70) return "text-yellow-600 bg-yellow-100";
    if (percentage >= 60) return "text-orange-600 bg-orange-100";
    return "text-red-600 bg-red-100";
  };

  const getGradeLetter = (percentage) => {
    if (percentage >= 90) return "A";
    if (percentage >= 80) return "B";
    if (percentage >= 70) return "C";
    if (percentage >= 60) return "D";
    return "F";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Calculate summary stats
  const totalQuizzes = history.length;
  const avgScore = totalQuizzes > 0 
    ? history.reduce((sum, q) => sum + q.percentage, 0) / totalQuizzes 
    : 0;
  const perfectScores = history.filter(q => q.percentage === 100).length;
  const totalPoints = history.reduce((sum, q) => sum + q.score, 0);

  return (
    <div className="min-h-screen bg-background" data-testid="quiz-history">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/student")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-foreground font-['Manrope']">Quiz History</h1>
              <p className="text-sm text-muted-foreground">Review your past quiz attempts</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-border/50">
            <CardContent className="p-4 text-center">
              <BarChart3 className="w-8 h-8 text-primary mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{totalQuizzes}</p>
              <p className="text-xs text-muted-foreground">Total Quizzes</p>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="p-4 text-center">
              <Trophy className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{avgScore.toFixed(1)}%</p>
              <p className="text-xs text-muted-foreground">Average Score</p>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{perfectScores}</p>
              <p className="text-xs text-muted-foreground">Perfect Scores</p>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="p-4 text-center">
              <Calendar className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{totalPoints}</p>
              <p className="text-xs text-muted-foreground">Total Points</p>
            </CardContent>
          </Card>
        </div>

        {/* Quiz History List */}
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="font-['Manrope']">All Quiz Attempts</CardTitle>
            <CardDescription>Click on a quiz to view details</CardDescription>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <div className="text-center py-12">
                <BarChart3 className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
                <p className="text-muted-foreground">No quiz history yet</p>
                <p className="text-sm text-muted-foreground">Start taking quizzes to see your history here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map((quiz, index) => (
                  <motion.div
                    key={quiz.result_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="p-3 md:p-4 rounded-xl border border-border/50 hover:border-primary/30 cursor-pointer transition-all group"
                    onClick={() => navigate(`/quiz/${quiz.quiz_id}`)}
                    data-testid={`history-item-${quiz.result_id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="font-semibold text-foreground text-sm md:text-base">{quiz.quiz_title}</h3>
                          {quiz.percentage === 100 && (
                            <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-[10px] md:text-xs">
                              <Trophy className="w-3 h-3 mr-0.5" />
                              Perfect
                            </Badge>
                          )}
                          {quiz.was_timed && (
                            <Badge variant="outline" className="text-orange-600 border-orange-200 text-[10px] md:text-xs">
                              <Clock className="w-3 h-3 mr-0.5" />
                              {quiz.time_limit}m
                            </Badge>
                          )}
                          {quiz.feedback && (
                            <Badge variant="outline" className="text-blue-600 border-blue-200 text-[10px] md:text-xs">
                              <MessageSquare className="w-3 h-3 mr-0.5" />
                              Feedback
                            </Badge>
                          )}
                          {quiz.ai_feedback && (
                            <Badge variant="outline" className="text-indigo-600 border-indigo-200 text-[10px] md:text-xs">
                              <Sparkles className="w-3 h-3 mr-0.5" />
                              AI
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 md:gap-4 text-xs md:text-sm text-muted-foreground flex-wrap">
                          <span className="truncate max-w-[150px] md:max-w-none">{quiz.class_name}</span>
                          <span className="hidden md:inline">•</span>
                          <span className="hidden md:inline">{quiz.subject}</span>
                          <span className="hidden md:inline">•</span>
                          <span>{format(new Date(quiz.submitted_at), "MMM d, yyyy")}</span>
                          {quiz.completion_time_seconds && (
                            <span className="text-blue-600 flex items-center gap-0.5">
                              <Clock className="w-3 h-3" />
                              {Math.floor(quiz.completion_time_seconds / 60)}m {quiz.completion_time_seconds % 60}s
                            </span>
                          )}
                        </div>
                        {(quiz.ai_feedback || quiz.feedback) && (
                          <div className="mt-2 space-y-2">
                            {quiz.ai_feedback && (
                              <div className="p-2 md:p-3 bg-indigo-50 border border-indigo-100 rounded-lg">
                                <p className="text-[10px] md:text-xs font-medium text-indigo-700 flex items-center gap-1 mb-1">
                                  <Sparkles className="w-3 h-3" />
                                  AI Study Feedback
                                </p>
                                <p className="text-xs md:text-sm text-indigo-900 whitespace-pre-wrap" data-testid={`ai-feedback-${quiz.result_id}`}>{quiz.ai_feedback}</p>
                              </div>
                            )}
                            {quiz.feedback && (
                              <div className="p-2 md:p-3 bg-blue-50 border border-blue-100 rounded-lg">
                                <p className="text-[10px] md:text-xs font-medium text-blue-700 flex items-center gap-1 mb-1">
                                  <MessageSquare className="w-3 h-3" />
                                  Teacher Feedback
                                </p>
                                <p className="text-xs md:text-sm text-blue-900 whitespace-pre-wrap">{quiz.feedback}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 md:gap-4 shrink-0">
                        <div className="text-right">
                          <div className={`text-base md:text-xl font-bold ${
                            quiz.percentage >= 70 ? "text-green-600" : 
                            quiz.percentage >= 50 ? "text-yellow-600" : "text-red-600"
                          }`}>
                            {quiz.score}/{quiz.total_points}
                          </div>
                          <div className="text-xs md:text-sm text-muted-foreground">{quiz.percentage}%</div>
                        </div>
                        <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center text-sm md:text-lg font-bold ${getScoreColor(quiz.percentage)}`}>
                          {getGradeLetter(quiz.percentage)}
                        </div>
                        <Button variant="ghost" size="icon" className="hidden sm:flex opacity-0 group-hover:opacity-100 transition-opacity">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
